import { useState, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { LIST_OF_VALUES_QUERY } from '../graphql/queries';

/**
 * useLov(category)
 * Returns { options, loading } where options is [{ value, label }]
 * sorted by sortOrder (already done by backend).
 */
export function useLov(category) {
  const { data, loading } = useQuery(LIST_OF_VALUES_QUERY, {
    variables: { category },
    fetchPolicy: 'cache-first',
  });

  return { options: data?.listOfValues || [], loading };
}

/**
 * useLovMany(categories)
 * Fetches multiple LOV categories in parallel using the Apollo client
 * imperatively — avoids calling hooks inside a loop.
 * Returns { lovMap: { [category]: [{value, label}] }, loading }
 */
export function useLovMany(categories) {
  const client = useApolloClient();
  const [lovMap, setLovMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Stable cache key so the effect only re-runs when the category list changes
  const cacheKey = categories.slice().sort().join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(
      categories.map((cat) =>
        client
          .query({ query: LIST_OF_VALUES_QUERY, variables: { category: cat }, fetchPolicy: 'cache-first' })
          .then((r) => [cat, r.data?.listOfValues || []])
      )
    ).then((entries) => {
      if (!cancelled) {
        setLovMap(Object.fromEntries(entries));
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [cacheKey]); // eslint-disable-line

  return { lovMap, loading };
}
